package com.essntl.features.message.data.repository

import com.essntl.core.supabase.getAll
import com.essntl.core.supabase.insert
import com.essntl.core.twilio.domain.TwilioClient
import com.essntl.core.utils.error.BadRequestException
import com.essntl.core.utils.repository.Repository
import com.essntl.core.utils.utils.capitalizeFirstLetter
import com.essntl.core.waypoint.domain.WaypointClient
import com.essntl.core.waypoint.domain.models.POServiceData
import com.essntl.core.waypoint.domain.models.POServicesVariables
import com.essntl.core.waypoint.domain.models.SimpleMessageVariables
import com.essntl.core.whatsapp.domain.WhatsappClient
import com.essntl.core.whatsapp.domain.models.WhapiGroupTemplate
import com.essntl.core.whatsapp.domain.models.WhapiTextMessageTemplate
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.message.data.supabase.message.MessageDto
import com.essntl.features.message.data.supabase.message.MessageSupabaseDataSource
import com.essntl.features.message.data.supabase.message.toRequestMap
import com.essntl.features.message.domain.model.MessageModel
import com.essntl.features.message.domain.model.WhapiChatModel
import com.essntl.features.message.domain.model.WhapiMessageModel
import com.essntl.features.message.domain.repository.MessageRepository
import core.config.buildFlavor
import core.dateformat.utils.formatTimestampToHHMM
import core.dateformat.utils.formatToFullString
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalTime
import org.koin.core.annotation.Single

@Single(binds = [MessageRepository::class])
class MessageRepositoryImpl internal constructor(
    private val messageDataSource: MessageSupabaseDataSource,
    private val waypoint: WaypointClient,
    private val twilio: TwilioClient,
    private val whatsapp: WhatsappClient,
) : Repository(), MessageRepository {

    override suspend fun sendMessage(message: MessageModel): Result<String> {
        return when (val type = message.type) {
            is MessageModel.Type.Email -> sendEmail(
                message,
                type.email, type.cc, type.from, type.bcc, type.replyTo, type.multiTemplateId, type.simpleTemplateId,
            )

            else -> Result.failure(Exception("Failed to send message"))
        }
    }

    override suspend fun getMessagesByItineraryServiceId(id: String): Result<List<MessageModel>> =
        runCatching {
            messageDataSource.getAll(
                columns = MessageDto.getColumns(),
            ) {
                filter {
                    eq("itinerary_service_id", id)
                }
            }
                .map {
                    it.toModel()
                }
        }

    override suspend fun getWhapiChatMessages(chatId: String): Result<List<WhapiMessageModel>> =
        whatsapp.getWhapiChatMessages(chatId = chatId)

    override suspend fun sendWhapiTextMessages(text: String, to: String): Result<WhapiMessageModel> =
        whatsapp.sendWhapiTextMessages(
            WhapiTextMessageTemplate(
                to = to,
                body = text,
            ),
        )

    override suspend fun getWhapiMessageById(whapiId: String): Result<WhapiMessageModel> =
        whatsapp.getWhapiMessageById(whapiId = whapiId)

    override suspend fun insertMessage(message: MessageModel): Result<MessageModel> =
        runCatching {
            val messageRequest = message.toRequestMap()
            messageDataSource
                .insert<MessageDto>(
                    requestMap = messageRequest,
                    columns = MessageDto.getColumns(),
                ).toModel()
        }

    override suspend fun getMessagesByWhapiIds(whapiIds: Set<String>): Result<List<MessageModel>> =
        runCatching {
            messageDataSource.getAll(
                columns = MessageDto.getColumns(),
            ) {
                filter {
                    if (whapiIds.isNotEmpty()) {
                        or {
                            whapiIds.forEach { id ->
                                eq("platform_id", id)
                            }
                        }
                    }
                }
            }
                .map {
                    it.toModel()
                }
        }

    override suspend fun reactToMessage(messageId: String, emoji: String): Result<Unit> =
        whatsapp.reactToMessage(messageId = messageId, emoji = emoji)

    override suspend fun checkContact(contactId: String): Result<Unit> =
        whatsapp.checkContact(contactId = contactId)

    override suspend fun sendContact(to: String): Result<Unit> =
        whatsapp.sendContact(to = to)

    override suspend fun createGroup(subject: String, phoneNumbers: List<String>): Result<WhapiChatModel> =
        whatsapp.createWhapiGroup(WhapiGroupTemplate(subject = subject, participants = phoneNumbers))

    override suspend fun updateGroupDescription(groupId: String, description: String): Result<Unit> =
        whatsapp.updateGroupDescription(groupId = groupId, description = description)

    override suspend fun addContactToGroup(groupId: String, participants: Set<String>): Result<Unit> =
        whatsapp.addContactToGroup(groupId = groupId, participants = participants)

    override suspend fun removeContactFromGroup(groupId: String, participants: Set<String>): Result<Unit> =
        whatsapp.removeContactFromGroup(groupId = groupId, participants = participants)

    private suspend fun sendEmail(
        message: MessageModel,
        email: String,
        cc: String?,
        from: String?,
        bcc: String?,
        replyTo: String?,
        multiTemplateId: String?,
        simpleTemplateId: String?,
    ): Result<String> {
        if (from.isNullOrEmpty() ||
            replyTo.isNullOrEmpty()
        ) return Result.failure(BadRequestException())

        if (message.itineraryServiceList.isEmpty()) {
            val template =
                WaypointClient.simpleEmailTemplate.copy(
                    templateId = simpleTemplateId,
                    to = email,
                    from = from,
                    bcc = bcc,
                    replyTo = replyTo,
                    variables = SimpleMessageVariables.empty.copy(
                        message = message.userText,
                        senderName = message.sender.prettyName,
                        senderEmail = message.sender.email ?: "-",
                        senderPhone = message.sender.phoneNumber ?: "-",
                    ),
                )

            val result = waypoint.sendEmail(template)
            return result
        }

        val messageData =
            // all value with "-" will allow the email template to hide the corresponding fields
            WaypointClient.multiPurchaseOrderTemplate.copy(
                templateId = multiTemplateId,
                to = email,
                cc = cc,
                from = from,
                bcc = bcc,
                replyTo = replyTo,
                variables =
                    POServicesVariables(
                        message = message.userText,
                        senderName = message.sender.prettyName,
                        senderEmail = message.sender.email ?: "-",
                        senderPhone = message.sender.phoneNumber ?: "-",
                        services =
                            message.itineraryServiceList
                                .sortedWith(
                                    compareBy<ItineraryServiceModel> {
                                        LocalDate.parse(it.startDate)
                                    }.thenBy {
                                        it.startTime?.let { time ->
                                            LocalTime.parse(formatTimestampToHHMM(time))
                                        }
                                    },
                                )
                                .mapNotNull { itineraryService ->
                                    val startTimePrettyString =
                                        itineraryService.startTime
                                            ?.let { formatTimestampToHHMM(it) }
                                            ?: "-"
                                    val endTimePrettyString =
                                        itineraryService.endTime
                                            ?.let { formatTimestampToHHMM(it) }
                                            ?: "-"

                                    val service =
                                        message.serviceList
                                            .firstOrNull { it.id == itineraryService.service.id }
                                            ?: return@mapNotNull null

                                    val category =
                                        message.categoryList
                                            .firstOrNull { it.id == service.category }
                                            ?: return@mapNotNull null

                                    val fullName = message.client.prettyName.capitalizeFirstLetter()

                                    POServiceData(
                                        clientId = message.client.id,
                                        clientName = fullName.trimIndent(),
                                        clientPhone = message.client.telephone ?: "-",
                                        guestCount = "${itineraryService.guestCount}",
                                        serviceId = service.id,
                                        category = category.name,
                                        serviceName = service.title.trimIndent(),
                                        supplierName = itineraryService.supplier?.prettyName ?: "-",
                                        date = LocalDate.parse(itineraryService.startDate).formatToFullString(),
                                        startTime = startTimePrettyString.replace(":00+00", ""),
                                        endTime = endTimePrettyString,
                                        meetingPoint = itineraryService.meetingPoint ?: "-",
                                        address = itineraryService.address?.description ?: "-",
                                        notes = itineraryService.specialNotes ?: "-",
                                        dynamicFields = itineraryService.dynamicFieldsString.ifEmpty { "-" },
                                    )
                                },
                    ),
            )

        val result = waypoint.sendEmail(messageData)
        return result
    }

    private suspend fun sendSms(
        message: MessageModel,
        phone: String,
    ): Result<Unit> {
        val service = message.serviceList.firstOrNull() ?: return Result.failure(BadRequestException())
        val proposalService = message.itineraryServiceList.firstOrNull() ?: return Result.failure(BadRequestException())

        val result = twilio.sendSms(
            to = phone,
            message =
                message.getClientMessageString(
                    service = service,
                    itineraryService = proposalService,
                ),
        )
        return result
    }
}
